// Catalog for the styles-first VTO.
//
// Primary source of truth: `/data/makeup-styles.json` from the main app.
// We fetch it at runtime so the Shade Finder gating and the VTO resolve the
// same style/product catalog. The bundled JSON import remains as an offline
// / dev fallback so the sub-app still boots if the sibling file is missing
// or temporarily unavailable.
//
// Backward compat: `?shade=<brand>-<slug>` deep-links from the main app's
// product cards still land here. We resolve the shade to whichever style
// contains it, so the user sees the curated look plus the exact product
// they clicked on pre-selected.

import stylesFile from './makeup-styles.json';

const VALID_FINISHES = ['matte', 'satin', 'glossy', 'metallic'] as const;
export type LipFinish = (typeof VALID_FINISHES)[number];

export type ProductTier = 'budget' | 'mid' | 'luxury';

export interface ProductMatch {
  brand: string;
  productName: string;
  tier: ProductTier;
  priceEur: number;
  hex: string;           // per-product colour (may differ slightly from style hex)
  affiliateUrl: string;
  shadeId: string;       // `<brand-slug>-<product-slug>`  — stable for deep-links
}

export interface KitSwatch {
  hex: string;
  finish: string;        // free-form (cream, shimmer, sheer, matte, satin, …)
  note: string;
}

export interface StyleKit {
  lipstickNote: string;  // adaptation note for skin tone (warm/cool/deep)
  blush: KitSwatch;
  eyeshadow: KitSwatch;
}

export interface StyleRationale {
  whyItWorks: string;
  skinFit: string;
  occasionFit: string;
  avoid: string;
}

export interface CosmeticMap {
  intent: string;
  focus: string[];
  paletteFamily: string;
  temperature: string;
  contrast: string;
  chroma: string;
  skinDepthFit: string;
  finishProfile: {
    skin: string;
    lips: string;
    eyes: string;
    cheeks: string;
  };
  placement: {
    eyes: string;
    cheeks: string;
    lips: string;
  };
  counterBrief: string[];
  riskControls: string[];
}

export interface MakeupStyle {
  id: string;
  name: string;
  tagline: string;
  mood: string[];
  occasion: string[];
  hex: string;
  finish: LipFinish;
  opacity: number;
  matches: ProductMatch[];
  kit?: StyleKit;               // optional for backwards compat with un-enriched data
  rationale?: StyleRationale;
  cosmeticMap?: CosmeticMap;
}

interface RawMatch {
  brand: string;
  productName: string;
  tier: string;
  priceEur: number;
  hex: string;
  affiliateUrl: string;
}

interface RawKitSwatch {
  hex: string;
  finish: string;
  note: string;
}

interface RawKit {
  lipstickNote: string;
  blush: RawKitSwatch;
  eyeshadow: RawKitSwatch;
}

interface RawRationale {
  whyItWorks: string;
  skinFit: string;
  occasionFit: string;
  avoid: string;
}

interface RawStyle {
  id: string;
  name: string;
  tagline: string;
  mood: string[];
  occasion: string[];
  hex: string;
  finish: string;
  opacity: number;
  matches: RawMatch[];
  kit?: RawKit;
  rationale?: RawRationale;
  cosmeticMap?: CosmeticMap;
}

interface RawStylesFile {
  looks: RawStyle[];
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function normaliseFinish(raw: string): LipFinish {
  const f = raw.toLowerCase();
  return (VALID_FINISHES as readonly string[]).includes(f) ? (f as LipFinish) : 'satin';
}

function normaliseTier(raw: string): ProductTier {
  if (raw === 'budget' || raw === 'mid' || raw === 'luxury') return raw;
  return 'mid';
}

function buildMatch(raw: RawMatch): ProductMatch {
  return {
    brand: raw.brand,
    productName: raw.productName,
    tier: normaliseTier(raw.tier),
    priceEur: raw.priceEur,
    hex: raw.hex,
    affiliateUrl: raw.affiliateUrl,
    shadeId: `${slugify(raw.brand)}-${slugify(raw.productName)}`
  };
}

const COSMETIC_MAPS: Record<string, CosmeticMap> = {
  'forever-young': {
    intent: 'Fresh gloss, effetto giovane e leggero',
    focus: ['lips', 'skin'],
    paletteFamily: 'rose nude / champagne',
    temperature: 'neutral-warm',
    contrast: 'low',
    chroma: 'soft',
    skinDepthFit: 'fair-light-medium',
    finishProfile: { skin: 'natural glow', lips: 'glossy sheer', eyes: 'soft shimmer', cheeks: 'cream diffused' },
    placement: { eyes: 'mobile lid molto leggero, crease quasi trasparente', cheeks: 'apples soft con drape corto', lips: 'bordo morbido, niente matita grafica' },
    counterBrief: ['smokey eye', 'matte scuro', 'contorno labbra marcato'],
    riskControls: ['cap shimmer chiaro', 'crease fade obbligatoria', 'lip edge soft']
  },
  'millennial-rose': {
    intent: 'Monocromia rosa polverosa universale',
    focus: ['balanced'],
    paletteFamily: 'dusty rose',
    temperature: 'neutral',
    contrast: 'low-medium',
    chroma: 'muted',
    skinDepthFit: 'light-medium',
    finishProfile: { skin: 'natural satin', lips: 'satin', eyes: 'matte taupe rose', cheeks: 'satin diffuse' },
    placement: { eyes: 'taupe rosato vicino alla piega', cheeks: 'guancia ampia e morbida', lips: 'bordo naturale satinato' },
    counterBrief: ['shimmer intenso', 'eyeliner nero grafico', 'blush brillante'],
    riskControls: ['preserve muted chroma', 'avoid overcontrast', 'soft cheek edge']
  },
  'bare-hours': {
    intent: 'Nude professionale, pulito e autorevole',
    focus: ['skin', 'lips'],
    paletteFamily: 'nude caramel',
    temperature: 'neutral-warm',
    contrast: 'low',
    chroma: 'muted',
    skinDepthFit: 'light-medium-tan',
    finishProfile: { skin: 'matte natural', lips: 'matte nude', eyes: 'matte neutral', cheeks: 'satin skinlike' },
    placement: { eyes: 'definizione minima su palpebra mobile', cheeks: 'salute naturale, non colore', lips: 'bordo pulito ma non scuro' },
    counterBrief: ['gloss', 'shimmer', 'accenti colore'],
    riskControls: ['adapt nude to depth', 'avoid grey nude', 'keep opacity moderate']
  },
  'soft-focus': {
    intent: 'Malva minimalista freddo, linea pulita',
    focus: ['balanced', 'lips'],
    paletteFamily: 'cool mauve',
    temperature: 'cool-neutral',
    contrast: 'medium-low',
    chroma: 'muted',
    skinDepthFit: 'fair-light-medium',
    finishProfile: { skin: 'soft matte', lips: 'matte mauve', eyes: 'cool matte taupe', cheeks: 'sheer mauve' },
    placement: { eyes: 'ombra fredda sottile sopra lash line', cheeks: 'velatura bassa saturazione', lips: 'bordo soft matte' },
    counterBrief: ['bronze warm', 'shimmer oro', 'corallo'],
    riskControls: ['warm skin adjustment', 'avoid ashy cast', 'low blush saturation']
  },
  'cherry-pop': {
    intent: 'Gloss ciliegia energico e luminoso',
    focus: ['lips'],
    paletteFamily: 'bright cherry / champagne',
    temperature: 'neutral-cool',
    contrast: 'medium-high',
    chroma: 'bright',
    skinDepthFit: 'universal',
    finishProfile: { skin: 'fresh glow', lips: 'glossy bright', eyes: 'champagne shimmer', cheeks: 'sheer warm pink' },
    placement: { eyes: 'luce champagne, niente profondità', cheeks: 'rosa caldo leggero', lips: 'bordo lucido controllato' },
    counterBrief: ['smokey', 'matte pesante', 'occhio serale'],
    riskControls: ['cap gloss highlight', 'keep eyes secondary', 'avoid red bleed']
  },
  'sunset-riot': {
    intent: 'Corallo caldo solare',
    focus: ['lips', 'cheeks'],
    paletteFamily: 'coral peach bronze',
    temperature: 'warm',
    contrast: 'medium',
    chroma: 'clear',
    skinDepthFit: 'warm-olive-deep',
    finishProfile: { skin: 'sunlit glow', lips: 'satin coral', eyes: 'warm bronze shimmer', cheeks: 'cream coral' },
    placement: { eyes: 'bronze caldo sfumato', cheeks: 'drape pesca verso centro viso', lips: 'corallo pieno satinato' },
    counterBrief: ['mauve freddo', 'berry cool', 'shimmer argento'],
    riskControls: ['cool undertone correction', 'avoid orange cast', 'preserve warmth']
  },
  'berry-whisper': {
    intent: 'Berry sofisticato, morbido e serale',
    focus: ['lips', 'eyes'],
    paletteFamily: 'soft berry plum',
    temperature: 'cool-neutral',
    contrast: 'medium',
    chroma: 'muted',
    skinDepthFit: 'medium-cool-deep',
    finishProfile: { skin: 'natural satin', lips: 'satin berry', eyes: 'matte plum brown', cheeks: 'sheer berry' },
    placement: { eyes: 'profondità plum controllata', cheeks: 'berry diluito', lips: 'bordo satin naturale' },
    counterBrief: ['oro intenso', 'rosso brillante', 'corallo'],
    riskControls: ['dilute on fair skin', 'avoid bruised cheek', 'keep shimmer off']
  },
  'velvet-autumn': {
    intent: 'Terracotta vellutata editoriale',
    focus: ['lips', 'cheeks'],
    paletteFamily: 'terracotta brick',
    temperature: 'warm-earthy',
    contrast: 'medium-high',
    chroma: 'rich-muted',
    skinDepthFit: 'warm-olive-deep',
    finishProfile: { skin: 'velvet matte', lips: 'matte brick', eyes: 'matte copper brown', cheeks: 'matte terracotta' },
    placement: { eyes: 'terra matte su mobile lid e crease', cheeks: 'mattone diffuso alto', lips: 'bordo matte definito' },
    counterBrief: ['cool pink', 'gloss', 'shimmer freddo'],
    riskControls: ['avoid muddy brown', 'cap cheek opacity', 'warm-only harmony']
  },
  'wine-oclock': {
    intent: 'Bordeaux serale raffinato',
    focus: ['lips'],
    paletteFamily: 'wine burgundy',
    temperature: 'cool',
    contrast: 'high',
    chroma: 'deep-muted',
    skinDepthFit: 'medium-olive-deep',
    finishProfile: { skin: 'natural satin', lips: 'satin wine', eyes: 'matte dark plum', cheeks: 'sheer wine' },
    placement: { eyes: 'struttura scura ma bassa', cheeks: 'ombra vinosa minima', lips: 'bordo controllato' },
    counterBrief: ['coral', 'bronze', 'gloss eccessivo'],
    riskControls: ['adapt on fair skin', 'preserve lip dimension', 'avoid flat dark lip']
  },
  'noir-classique': {
    intent: 'Prugna profondo, drammatico e adulto',
    focus: ['lips', 'eyes'],
    paletteFamily: 'deep plum',
    temperature: 'cool',
    contrast: 'very-high',
    chroma: 'deep',
    skinDepthFit: 'medium-deep',
    finishProfile: { skin: 'soft matte', lips: 'matte deep plum', eyes: 'matte smokey plum', cheeks: 'matte sheer berry' },
    placement: { eyes: 'smokey basso sfumato', cheeks: 'guancia-ombra', lips: 'bordo pieno ma non piatto' },
    counterBrief: ['highlighter visibile', 'gloss', 'corallo'],
    riskControls: ['cap dark coverage', 'restore lip luminosity', 'avoid cadaveric blush']
  },
  regina: {
    intent: 'Rosso classico protagonista',
    focus: ['lips'],
    paletteFamily: 'classic red / warm neutral',
    temperature: 'neutral',
    contrast: 'high',
    chroma: 'clear',
    skinDepthFit: 'universal',
    finishProfile: { skin: 'natural polished', lips: 'matte classic red', eyes: 'matte warm neutral', cheeks: 'satin peach' },
    placement: { eyes: 'occhio arretrato, crease neutra', cheeks: 'pesca caldo per umanizzare', lips: 'bordo rosso definito' },
    counterBrief: ['smokey', 'shimmer occhi', 'highlighter evidente'],
    riskControls: ['lip border precision', 'keep eyes quiet', 'undertone red shift']
  },
  malizia: {
    intent: 'Rosso glossy glamour, luce su luce',
    focus: ['lips', 'skin'],
    paletteFamily: 'deep glossy red / rose gold',
    temperature: 'neutral-warm',
    contrast: 'high',
    chroma: 'rich',
    skinDepthFit: 'universal',
    finishProfile: { skin: 'glow satin', lips: 'glossy deep red', eyes: 'rose-gold shimmer', cheeks: 'satin peach' },
    placement: { eyes: 'oro rosato su mobile lid con crease morbida', cheeks: 'satin glow diffuso', lips: 'gloss rosso controllato' },
    counterBrief: ['matte totale', 'pelle spenta', 'nude eye senza luce'],
    riskControls: ['avoid sticker shimmer', 'cap gloss highlight', 'crease fade required']
  },
  'dark-gothic': {
    intent: 'Black plum statement, alternativo',
    focus: ['lips', 'eyes'],
    paletteFamily: 'black plum',
    temperature: 'cool',
    contrast: 'extreme',
    chroma: 'deep-muted',
    skinDepthFit: 'medium-light-deep',
    finishProfile: { skin: 'natural matte', lips: 'matte black plum', eyes: 'matte black plum smoke', cheeks: 'cold sheer pink' },
    placement: { eyes: 'smokey sfumato vicino alla lash line', cheeks: 'minimo anti-cadaverico', lips: 'bordo scuro pieno con texture visibile' },
    counterBrief: ['oro', 'pesca caldo', 'blush acceso'],
    riskControls: ['cap black opacity', 'restore lip shape', 'protect brow bridge']
  }
};

function buildStyle(raw: RawStyle): MakeupStyle {
  return {
    id: raw.id,
    name: raw.name,
    tagline: raw.tagline,
    mood: raw.mood,
    occasion: raw.occasion,
    hex: raw.hex,
    finish: normaliseFinish(raw.finish),
    opacity: raw.opacity,
    matches: raw.matches.map(buildMatch),
    kit: raw.kit,
    rationale: raw.rationale,
    cosmeticMap: raw.cosmeticMap ?? COSMETIC_MAPS[raw.id]
  };
}

let cached: MakeupStyle[] | null = null;

export async function loadStyles(): Promise<MakeupStyle[]> {
  if (cached) return cached;
  let file = stylesFile as RawStylesFile;
  try {
    const response = await fetch('/data/makeup-styles.json');
    if (response.ok) {
      file = (await response.json()) as RawStylesFile;
    }
  } catch {
    // Offline/dev fallback: keep using the bundled snapshot.
  }
  cached = file.looks.map(buildStyle);
  return cached;
}

export function findStyleById(
  styles: MakeupStyle[],
  id: string | null
): MakeupStyle | null {
  if (!id) return null;
  return styles.find((s) => s.id === id.toLowerCase()) ?? null;
}

// Resolve a `?shade=<brand-slug>-<product-slug>` deep-link into (style, match).
// Returns the style that contains the product and which match in that style
// was hit, so the UI can pre-select both.
export function findStyleByShadeId(
  styles: MakeupStyle[],
  shadeId: string | null
): { style: MakeupStyle; match: ProductMatch } | null {
  if (!shadeId) return null;
  const target = shadeId.toLowerCase();
  for (const style of styles) {
    const match = style.matches.find((m) => m.shadeId === target);
    if (match) return { style, match };
  }
  return null;
}

// Helpers the renderer needs: get the hex/finish/opacity to paint for a
// given style, optionally overridden by a specific product match the user
// picked from the shop-the-look list.
export function resolveRenderable(
  style: MakeupStyle,
  activeMatch: ProductMatch | null
): { hex: string; finish: LipFinish; opacity: number } {
  const hex = activeMatch?.hex ?? style.hex;
  // Per-product finish isn't tracked yet — assume the style's finish applies
  // to all its matches. When shade-colors.json gains per-product finish we
  // can override here.
  return { hex, finish: style.finish, opacity: style.opacity };
}

export function capitaliseBrand(brand: string): string {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}
